<script setup>
import ToggleApikomContentPendedAtAction from '@frontJs/components/actions/projectManagement/seo/ToggleApikomContentPendedAtAction.vue';
import ToggleApikomContentProcessedAtAction from '@frontJs/components/actions/projectManagement/seo/ToggleApikomContentProcessedAtAction.vue';
import UpdateTenantECommerceProductContentAction from '@frontJs/components/actions/projectManagement/seo/UpdateTenantECommerceProductContentAction.vue';
import CheckIcon from '@iconify-vue/ph/check';
import ClockIcon from '@iconify-vue/ph/clock';
import NotePencilIcon from '@iconify-vue/ph/note-pencil';
import PencilSimpleIcon from '@iconify-vue/ph/pencil-simple';
import XMarkIcon from '@iconify-vue/ph/x';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import ExportButton from '@js/components/buttons/ExportButton.vue';
import DataTableLayout from '@js/components/tables/DataTableLayout.vue';
import BaseCell from '@js/components/tables/cells/BaseCell.vue';
import HeaderCell from '@js/components/tables/cells/HeaderCell.vue';
import LinkCell from '@js/components/tables/cells/LinkCell.vue';
import NumberCell from '@js/components/tables/cells/NumberCell.vue';
import PillCell from '@js/components/tables/cells/PillCell.vue';
import PriceCell from '@js/components/tables/cells/PriceCell.vue';
import StatusCell from '@js/components/tables/cells/StatusCell.vue';
import TextCell from '@js/components/tables/cells/TextCell.vue';
import { useDataTable } from '@js/utils/dataTable';
import { formatDate } from '@js/utils/formatter';
import { useRoute } from '@js/utils/route';
import { createColumnHelper } from '@tanstack/vue-table';
import { h } from 'vue';

const {
    tenant,
    userSessionKey,
    withoutShortDescription,
    withoutDescription,
    withoutMetaTitle,
    withoutMetaDescription,
    updated,
    standard,
    premium,
    useless,
    hideNotDoneSeoApikomContent,
} = defineProps({
    tenant: {
        type: Object,
        required: true,
    },
    userSessionKey: {
        type: String,
        required: false,
    },
    withoutShortDescription: {
        type: Boolean,
        required: false,
    },
    withoutDescription: {
        type: Boolean,
        required: false,
    },
    withoutMetaTitle: {
        type: Boolean,
        required: false,
    },
    withoutMetaDescription: {
        type: Boolean,
        required: false,
    },
    updated: {
        type: Boolean,
        required: false,
    },
    standard: {
        type: Boolean,
        required: false,
    },
    premium: {
        type: Boolean,
        required: false,
    },
    useless: {
        type: Boolean,
        required: false,
    },
    hideNotDoneSeoApikomContent: {
        type: Boolean,
        required: false,
    },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
    loading,
    table,
    count,
    globalFilter,
    filters,
    availableFilters,
    setGlobalFilter,
    setFilters,
    updateData,
    selectionIds,
    exportData,
} = useDataTable({
    columns: [
        columnHelper.accessor(() => null, {
            id: 'cmsProductId',
            header: () => 'ID CMS',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.cmsProductId,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'sku',
            header: () => 'SKU',
            cell: (props) =>
                h(TextCell, {
                    text: props.row.original.sku,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'active',
            header: () => 'Actif',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.active,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'name',
            header: () => 'Nom',
            cell: (props) =>
                h(LinkCell, {
                    text: props.row.original.languageName,
                    href: props.row.original.languageUrl,
                    length: 140,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'finalPriceInclTax',
            header: () => 'Prix',
            cell: (props) =>
                h(PriceCell, {
                    price: props.row.original.finalPriceInclTax,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'realQuantity',
            header: () => 'Qté',
            cell: (props) =>
                h(NumberCell, {
                    number: props.row.original.quantity,
                }),
        }),
        columnHelper.accessor(() => null, {
            id: 'description',
            header: () => 'Description',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.hasDescription,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'metaTitle',
            header: () => 'Méta-titre',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.hasMetaTitle,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'metaDescription',
            header: () => 'Méta-description',
            cell: (props) =>
                h(StatusCell, {
                    status: props.row.original.hasMetaDescription,
                }),
            enableSorting: false,
        }),
        columnHelper.accessor(() => null, {
            id: 'contentType',
            header: () => 'Type de contenu',
            cell: (props) =>
                props.row.original.seoContentProduct &&
                props.row.original.seoContentProduct.contentType
                    ? h(PillCell, {
                          pillValue:
                              props.row.original.seoContentProduct.contentType
                                  .label,
                          type:
                              props.row.original.seoContentProduct.contentType
                                  .value === 'useless'
                                  ? 'warning'
                                  : 'primary',
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'descriptionUpdatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Description<br/>MAJ le',
                }),
            cell: (props) =>
                props.row.original.seoContentProduct &&
                props.row.original.seoContentProduct.descriptionUpdatedAt
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.seoContentProduct
                                  .descriptionUpdatedAt,
                          ),
                      })
                    : null,
        }),
        columnHelper.accessor(() => null, {
            id: 'metaUpdatedAt',
            headerCustom: true,
            header: (props) =>
                h(HeaderCell, {
                    header: props.header,
                    title: 'Meta<br/>MAJ le',
                }),
            cell: (props) =>
                props.row.original.seoContentProduct &&
                props.row.original.seoContentProduct.metaUpdatedAt
                    ? h(TextCell, {
                          text: formatDate(
                              props.row.original.seoContentProduct
                                  .metaUpdatedAt,
                          ),
                      })
                    : null,
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            width: 0,
            cell: (props) =>
                h(
                    BaseCell,
                    {},
                    {
                        default: () => [
                            props.row.original.seoApikomContent &&
                            props.row.original.seoApikomContent.processedAt ===
                                null
                                ? h(
                                      ToggleApikomContentPendedAtAction,
                                      {
                                          tenant,
                                          seoApikomContent:
                                              props.row.original
                                                  .seoApikomContent,
                                          callback: updateData,
                                      },
                                      {
                                          button: ({ action }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  callback: () => action(),
                                                  title: props.row.original
                                                      .seoApikomContent.pendedAt
                                                      ? 'Détraiter'
                                                      : 'Mettre en attente',
                                                  icon: props.row.original
                                                      .seoApikomContent.pendedAt
                                                      ? XMarkIcon
                                                      : ClockIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.seoApikomContent
                                ? h(
                                      ToggleApikomContentProcessedAtAction,
                                      {
                                          tenant,
                                          seoApikomContent:
                                              props.row.original
                                                  .seoApikomContent,
                                          callback: updateData,
                                      },
                                      {
                                          button: ({ action }) =>
                                              h(ActionButton, {
                                                  size: 'small',
                                                  type: 'secondary',
                                                  square: true,
                                                  callback: () => action(),
                                                  title: props.row.original
                                                      .seoApikomContent
                                                      .processedAt
                                                      ? 'Détraiter'
                                                      : 'Traiter',
                                                  icon: props.row.original
                                                      .seoApikomContent
                                                      .processedAt
                                                      ? XMarkIcon
                                                      : CheckIcon,
                                              }),
                                      },
                                  )
                                : null,
                            props.row.original.seoApikomContent
                                ? h(ActionButton, {
                                      size: 'small',
                                      square: true,
                                      title: 'Modifier',
                                      icon: PencilSimpleIcon,
                                      href: route(
                                          'front.projectManagement.seo.updateApikomContent',
                                          {
                                              tenant: tenant.id,
                                              id: props.row.original
                                                  .seoApikomContent.id,
                                          },
                                      ),
                                  })
                                : null,
                            h(
                                UpdateTenantECommerceProductContentAction,
                                {
                                    tenant,
                                    eCommerceProduct: props.row.original,
                                    callback: updateData,
                                },
                                {
                                    trigger: () =>
                                        h(ActionButton, {
                                            size: 'small',
                                            square: true,
                                            icon: NotePencilIcon,
                                            type: 'primary',
                                        }),
                                },
                            ),
                        ],
                    },
                ),
        }),
    ],
    url: route('front.projectManagement.seo.postECommerceProductContents', {
        tenant: tenant.id,
    }),
    exportUrl: route(
        'front.projectManagement.seo.postExportECommerceProductContents',
        {
            tenant: tenant.id,
        },
    ),
    userSessionKey,
    data: {
        withoutShortDescription,
        withoutDescription,
        withoutMetaTitle,
        withoutMetaDescription,
        updated,
        standard,
        premium,
        useless,
        hideNotDoneSeoApikomContent,
    },
    selectionIdKey: 'cmsProductId',
    exportSelectionIdKey: 'eCommerceProducts.cmsProductId',
    columnPinning: {
        right: ['actions'],
    },
    columnVisibility: {
        contentType: !(standard || premium || useless),
    },
});
</script>

<template>
    <DataTableLayout
        :table="table"
        :count="count"
        :global-filter="globalFilter"
        :filters="filters"
        :available-filters="availableFilters"
        :set-filters="setFilters"
        :set-global-filter="setGlobalFilter"
        :loading="loading"
    >
        <template #actions>
            <ExportButton
                :csv-export-callback="() => exportData('csv')"
                :xlsx-export-callback="() => exportData('xlsx')"
                :disabled="selectionIds.length === 0"
            />
        </template>
    </DataTableLayout>
</template>
